// frontend/src/dashboard/HomePage.tsx
//
// Worqly dashboard home — operational snapshot.
//
// Layout (premium-dashboards "Hero asymmetric" recipe)
//   ┌────────────────────────────────────────────────────────────────┐
//   │ Hero greeting   ·   Date · Company           CTAs              │
//   │ KPI strip (4 tiles)                                            │
//   │ ┌─────────────────────────────────┬────────────────────────┐   │
//   │ │ Revenue bars (12 weeks)         │ Active timer (LocalT.)│   │
//   │ ├─────────────────────────────────┼────────────────────────┤   │
//   │ │ Onboarding (dark contrast isl.) │ Open invoices         │   │
//   │ └─────────────────────────────────┴────────────────────────┘   │
//   │ Weekly cash · Paid ratio · Upgrade promo                       │
//   └────────────────────────────────────────────────────────────────┘
//
// All data is real — wired to `/api/invoicing/invoices`, `/api/payments`,
// `/api/hr/employees`, `/api/projects/projects` and
// `/api/projects/time-entries/me/open` (LocalTimer). No fabricated data.
//
// Dark + light verified for every surface.

import * as React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowUpRight, ArrowDownRight, Plus,
  FileText, Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../core/auth/AuthProvider';
import { usePermission } from '../core/auth/usePermission';
import { useBranding } from '../core/branding/BrandingProvider';
import { useApi } from '../design-system/useApi';
import { formatMoney } from '../design-system/format';
import {
  PageShell, GlassCard, DottedPromoCard,
  BentoGrid, Eyebrow, ButtonLink, IconButton,
  StatusChip,
} from '../design-system/pds';
import { RevenueBars, DottedBarChart, CircularTimer, GaugeArcStat } from '../design-system/charts';
import { QuickBooksFinanceSection } from '../modules/integrations/components/QuickBooksFinanceSection';
import { GetStartedPanel } from '../modules/onboarding/GetStartedPanel';
import { AgentSetupCard } from '../modules/monitoring/components/AgentSetupCard';

/* ─────────────────────────── helpers ─────────────────────────── */

function greeting(now: Date): string {
  const h = now.getHours();
  if (h < 5)  return 'Burning the late shift';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 22) return 'Good evening';
  return 'Working late';
}

function dateLine(now: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  }).format(now);
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_MS  = 24 * 60 * 60 * 1000;

/* ──────────────────────────── invoice type ─────────────────────────── */

type InvoiceRow = {
  id?: string;
  status: string;
  total: string | number;
  due_date?: string;
  sent_at?: string;
  paid_at?: string | null;
  client_name?: string;
  number?: string;
};

type PaymentRow = {
  amount: string | number;
  currency: string;
  /** /api/payments rows carry paid_at (payments.service SELECT *) — NOT received_at. */
  paid_at: string;
};

type EmployeeRow = { is_active?: boolean; name?: string };
type ProjectRow  = { status: string; name?: string };

/* ────────────────────────────── page ────────────────────────────── */

export function HomePage() {
  const { identity } = useAuth();
  const { tenant } = useBranding();
  const now = React.useMemo(() => new Date(), []);

  const userEmail = identity?.type === 'user' ? identity.email : '';
  const userName  = identity?.type === 'user' ? identity.name : '';
  const handle    = userName?.split(' ')[0] || userEmail.split('@')[0];
  const companyName = tenant?.company_name ?? 'your workspace';

  // ── permission gates (mirror DashboardShell's NAV_GROUPS keys exactly) ──
  const canInvoices   = usePermission('invoices.view');
  const canPayments   = usePermission('payments.manage');
  const canTeam       = usePermission('hr.employees.view');
  const canProjects   = usePermission('projects.view');
  const canNewInvoice = usePermission('invoices.write');

  // ── data hooks — skipped (null path) when the user lacks the gate, so no
  // 403 is ever fired for a self-service employee. ───────────────────────
  const invQ  = useApi<{ invoices: InvoiceRow[] }>(canInvoices ? '/api/invoicing/invoices?limit=200' : null);
  const payQ  = useApi<{ rows: PaymentRow[] }>(canPayments ? '/api/payments?limit=200' : null);
  const empQ  = useApi<{ employees: EmployeeRow[] }>(canTeam ? '/api/hr/employees' : null);
  const projQ = useApi<{ projects: ProjectRow[] }>(canProjects ? '/api/projects/projects?status=active' : null);

  const invoices = invQ.data?.invoices ?? [];
  const openInvList = invoices.filter((r) => r.status === 'sent' || r.status === 'overdue');
  const openInv     = openInvList.length;
  const overdueInv  = invoices.filter((r) => r.status === 'overdue').length;
  const paidInv     = invoices.filter((r) => r.status === 'paid').length;
  const totalInv    = invoices.length;
  const collectedRatio = totalInv === 0 ? 0 : paidInv / totalInv;

  const payments = payQ.data?.rows ?? [];
  const currency = payments[0]?.currency?.toUpperCase() ?? 'USD';

  // 12-week revenue bars
  const series = React.useMemo(() => {
    const buckets = new Array(12).fill(0);
    const cutoff = Date.now() - 12 * WEEK_MS;
    for (const p of payments) {
      const t = new Date(p.paid_at).getTime();
      if (!Number.isFinite(t) || t < cutoff) continue;
      const weekIdx = Math.min(11, Math.floor((t - cutoff) / WEEK_MS));
      buckets[weekIdx] += Number(p.amount) || 0;
    }
    return buckets;
  }, [payments]);

  const totalRevenue = payments.reduce((s, r) => s + Number(r.amount), 0);
  const thisMonth = payments
    .filter((p) => {
      const t = new Date(p.paid_at).getTime();
      return Number.isFinite(t) && t > Date.now() - 30 * DAY_MS;
    })
    .reduce((s, r) => s + Number(r.amount), 0);
  const prevMonth = payments
    .filter((p) => {
      const t = new Date(p.paid_at).getTime();
      return Number.isFinite(t) && t > Date.now() - 60 * DAY_MS && t <= Date.now() - 30 * DAY_MS;
    })
    .reduce((s, r) => s + Number(r.amount), 0);
  const monthDelta = prevMonth === 0 ? null : ((thisMonth - prevMonth) / prevMonth) * 100;

  const employees = empQ.data?.employees ?? [];
  const activeEmployees = employees.filter((e) => e.is_active !== false).length;
  const activeProjects  = (projQ.data?.projects ?? []).length;

  // Weekly bars from real payments (last 7 days, day-of-week buckets).
  const weeklyBars = React.useMemo(() => {
    const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const buckets = new Array(7).fill(0);
    for (const p of payments) {
      const t = new Date(p.paid_at);
      if (!Number.isFinite(t.getTime())) continue;
      const since = (Date.now() - t.getTime()) / DAY_MS;
      if (since > 7) continue;
      buckets[t.getDay()] += Number(p.amount) || 0;
    }
    const max = Math.max(1, ...buckets);
    let topIdx = 0;
    buckets.forEach((v, i) => { if (v > buckets[topIdx]) topIdx = i; });
    const anyValue = buckets.some((v) => v > 0);
    return buckets.map((v, i) => ({
      label: dayNames[i],
      value: max ? (v / max) * 100 : 5,
      highlight: anyValue && i === topIdx && v > 0,
      tooltip: anyValue && i === topIdx && v > 0 ? formatMoney(v, currency) : undefined,
    }));
  }, [payments, currency]);

  const sevenDayTotal = payments
    .filter((p) => {
      const t = new Date(p.paid_at).getTime();
      return Number.isFinite(t) && t > Date.now() - 7 * DAY_MS;
    })
    .reduce((s, r) => s + Number(r.amount), 0);

  // Hero snapshot sentence — composed ONLY from feeds this user is permitted
  // to see. With proper gating, a permitted feed should never 403; a
  // surviving error means a genuine failure (network, server) for a feed we
  // do have rights to.
  const anyPermittedError =
    (canInvoices && !!invQ.error) || (canProjects && !!projQ.error) || (canTeam && !!empQ.error);
  const snapshotParts: string[] = [];
  if (canInvoices) snapshotParts.push(openInv > 0 ? `${openInv} open invoice${openInv === 1 ? '' : 's'}` : 'No open invoices');
  if (canProjects) snapshotParts.push(`${activeProjects} active project${activeProjects === 1 ? '' : 's'}`);
  if (canTeam) snapshotParts.push(`${activeEmployees} on the team`);

  /* ───────────────────────────── render ───────────────────────────── */

  return (
    <PageShell>
      {/* HERO ───────────────────────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 110, damping: 22 }}
        className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-end mb-10"
      >
        <div>
          <Eyebrow className="mb-2">{dateLine(now)} · {companyName}</Eyebrow>
          <h1 className="font-light text-display-page text-ink dark:text-white">
            {greeting(now)}, <span className="text-coral">{handle || 'there'}</span>.
          </h1>
          <p className="text-[15px] leading-relaxed text-ink/60 dark:text-white/60 mt-4 max-w-xl">
            {anyPermittedError ? (
              // Don't fabricate zero-counts when a permitted feed genuinely
              // failed to load — say so instead.
              'A snapshot of your operations. Some numbers couldn’t load for your account.'
            ) : snapshotParts.length > 0 ? (
              <>A snapshot of your operations. {snapshotParts.join(', ')}.</>
            ) : (
              'A snapshot of your operations.'
            )}
          </p>
        </div>
        {(canNewInvoice || canTeam) && (
          <div className="flex items-center gap-2.5 flex-wrap">
            {canNewInvoice && (
              <ButtonLink to="/dashboard/invoices/new" variant="primary" size="md">
                <Plus className="h-3.5 w-3.5" />
                New invoice
              </ButtonLink>
            )}
            {canTeam && (
              <ButtonLink to="/dashboard/hr/team" variant="secondary" size="md">
                <Users className="h-3.5 w-3.5" />
                Manage team
              </ButtonLink>
            )}
          </div>
        )}
      </motion.section>

      {/* KPI strip ──────────────────────────────────────────────────── */}
      {(canPayments || canInvoices || canTeam || canProjects) && (
        <motion.section
          initial="hidden"
          animate="visible"
          variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
        >
          {canPayments && (
            <KpiTile
              label="Collected"
              value={formatMoney(totalRevenue, currency)}
              sub={`${formatMoney(thisMonth, currency)} this month`}
              delta={monthDelta}
              errored={!!payQ.error}
            />
          )}
          {canInvoices && (
            <KpiTile
              label="Open invoices"
              value={openInv.toLocaleString()}
              sub={`${paidInv} paid · ${overdueInv} overdue`}
              accent="coral"
              errored={!!invQ.error}
            />
          )}
          {canTeam && (
            <KpiTile
              label="Team"
              value={activeEmployees.toLocaleString()}
              sub={
                employees.length - activeEmployees > 0
                  ? `${employees.length - activeEmployees} inactive`
                  : 'All active'
              }
              errored={!!empQ.error}
            />
          )}
          {canProjects && (
            <KpiTile
              label="Active projects"
              value={activeProjects.toLocaleString()}
              sub="Across all clients"
              errored={!!projQ.error}
            />
          )}
        </motion.section>
      )}

      {/* MAIN BENTO ─────────────────────────────────────────────────── */}
      <BentoGrid cols={12} className="mb-6">
        {/* Revenue chart — wide */}
        {canPayments && (
        <GlassCard className="col-span-12 lg:col-span-8" padding="p-7" motionDelay={0.05}>
          <div className="flex items-start justify-between mb-6">
            <div>
              <Eyebrow className="mb-2">Revenue · last 12 weeks</Eyebrow>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className={[
                  'text-stat-lg tabular-nums',
                  payQ.error ? 'text-ink/30 dark:text-white/30' : 'text-ink dark:text-white',
                ].join(' ')}>
                  {payQ.error ? '—' : formatMoney(totalRevenue, currency)}
                </span>
                {!payQ.error && monthDelta !== null && <DeltaPill delta={monthDelta} />}
              </div>
              <p className="text-[13px] text-ink/55 dark:text-white/55 mt-2">
                {payQ.error
                  ? 'Payment data couldn’t load for your account'
                  : `${formatMoney(thisMonth, currency)} collected in the last 30 days`}
              </p>
            </div>
            <Link to="/dashboard/payments" aria-label="Open payments">
              <IconButton icon={<ArrowUpRight className="h-4 w-4" />} variant="secondary" size="sm" />
            </Link>
          </div>
          <RevenueBars series={series} height={150} />
          <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-ink/45 dark:text-white/45 uppercase tracking-[0.16em]">
            <span>12 weeks ago</span>
            <span>This week</span>
          </div>
        </GlassCard>
        )}

        {/* Active session — LocalTimer preserved */}
        <GlassCard className="col-span-12 lg:col-span-4" padding="p-7" motionDelay={0.10}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <Eyebrow className="mb-2">Today</Eyebrow>
              <p className="text-title-lg text-ink dark:text-white">Active session</p>
            </div>
            <Link to="/dashboard/projects" aria-label="Open projects">
              <IconButton icon={<ArrowUpRight className="h-4 w-4" />} variant="secondary" size="sm" />
            </Link>
          </div>
          <div className="flex flex-col items-center justify-center pt-2">
            <LocalTimer />
          </div>
        </GlassCard>

        <GetStartedPanel />

        {/* Monitored employees: install/setup card for the desktop agent
            (renders nothing for everyone else). */}
        <div className="col-span-12">
          <AgentSetupCard />
        </div>

        {/* Open invoices snapshot */}
        {canInvoices && (
        <GlassCard className="col-span-12 lg:col-span-5" padding="p-7" motionDelay={0.20}>
          <div className="flex items-start justify-between mb-5">
            <div>
              <Eyebrow className="mb-2">Inbox</Eyebrow>
              <p className="text-title-lg text-ink dark:text-white">Open invoices</p>
            </div>
            <Link to="/dashboard/invoices" aria-label="Open invoices">
              <IconButton icon={<ArrowUpRight className="h-4 w-4" />} variant="secondary" size="sm" />
            </Link>
          </div>
          <div className="space-y-2">
            {openInvList.slice(0, 5).map((inv, i) => (
              <Link
                key={inv.id ?? i}
                to="/dashboard/invoices"
                className={[
                  'flex items-center justify-between gap-3 py-2.5 px-3 rounded-2xl',
                  'bg-white/55 hover:bg-white/80 transition-colors',
                  'dark:bg-white/[0.05] dark:hover:bg-white/[0.09]',
                ].join(' ')}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileText className="h-3.5 w-3.5 text-ink/45 dark:text-white/45 shrink-0" />
                  <span className="text-[13px] text-ink dark:text-white font-semibold truncate">
                    {inv.client_name ?? inv.number ?? `Invoice ${i + 1}`}
                  </span>
                  <span className="text-[12px] text-ink/55 dark:text-white/55 tabular-nums shrink-0">
                    {formatMoney(inv.total, currency)}
                  </span>
                </div>
                <StatusChip status={inv.status} />
              </Link>
            ))}

            {invQ.error && (
              <p className="text-[13px] text-ink/60 dark:text-white/60 px-3 py-2">
                Invoices couldn’t load for your account.
              </p>
            )}

            {invoices.length === 0 && !invQ.loading && !invQ.error && (
              <Link
                to="/dashboard/invoices/new"
                className={[
                  'block py-3 px-3 rounded-2xl',
                  'border border-dashed border-ink/15 dark:border-white/15',
                  'hover:border-coral hover:bg-white/40 dark:hover:bg-white/[0.06]',
                  'transition-colors',
                ].join(' ')}
              >
                <p className="text-[13px] text-ink/60 dark:text-white/60">No invoices yet</p>
                <p className="text-[13.5px] font-bold text-ink dark:text-white inline-flex items-center gap-1 mt-1">
                  Send your first <ArrowUpRight className="h-3.5 w-3.5" />
                </p>
              </Link>
            )}

            {openInv === 0 && invoices.length > 0 && (
              <p className="text-[13px] text-ink/60 dark:text-white/60 px-3 py-2">
                All caught up — no open invoices.
              </p>
            )}

            {overdueInv > 0 && (
              <p className="text-[12px] text-rose-600 dark:text-rose-300 font-semibold mt-2 px-3">
                {overdueInv} overdue · follow up today
              </p>
            )}
          </div>
        </GlassCard>
        )}
      </BentoGrid>

      {/* SECONDARY ROW ──────────────────────────────────────────────── */}
      <BentoGrid cols={12} className="mb-6">
        {/* Weekly cash flow */}
        {canPayments && (
        <GlassCard className="col-span-12 lg:col-span-6" padding="p-7" motionDelay={0.05}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <Eyebrow className="mb-2">Cash flow · last 7 days</Eyebrow>
              <p className={[
                'text-stat-hero font-light leading-none tabular-nums',
                payQ.error ? 'text-ink/30 dark:text-white/30' : 'text-ink dark:text-white',
              ].join(' ')}>
                {payQ.error ? '—' : formatMoney(sevenDayTotal, currency)}
              </p>
              <p className="text-[13px] text-ink/55 dark:text-white/55 mt-1.5">
                {payQ.error
                  ? 'Couldn’t load for your account'
                  : sevenDayTotal > 0 ? 'Collected in the last week' : 'No collections this week'}
              </p>
            </div>
          </div>
          <DottedBarChart data={weeklyBars} height={140} />
        </GlassCard>
        )}

        {/* Paid ratio */}
        {canInvoices && (
        <GlassCard className="col-span-12 sm:col-span-6 lg:col-span-3" padding="p-7" motionDelay={0.10}>
          <Eyebrow className="mb-3">Paid ratio</Eyebrow>
          {invQ.error ? (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="font-bold text-stat-md leading-none text-ink/30 dark:text-white/30">—</p>
              <p className="text-[12px] text-ink/55 dark:text-white/55 text-center mt-3">
                Invoice data couldn’t load for your account
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center py-2">
                <GaugeArcStat
                  value={Math.round(collectedRatio * 100)}
                  label="Closed"
                  size={160}
                />
              </div>
              <p className="text-[12px] text-ink/55 dark:text-white/55 text-center mt-3">
                {paidInv} of {totalInv || 0} invoices paid
              </p>
            </>
          )}
        </GlassCard>
        )}

        {/* Plan management — honest copy: there is no "Premium" SKU, billing is
            per-module + per-seat, managed on the billing page. Self-service:
            every tenant user can see their own workspace's plan/billing page. */}
        <DottedPromoCard
          className="col-span-12 sm:col-span-6 lg:col-span-3"
          title="Your plan"
          body="Turn modules on or off and adjust seats any time — you pay only for what you use."
          ctaLabel="Manage plan"
          to="/dashboard/billing"
        />
      </BentoGrid>

      {/* QUICKBOOKS FINANCIALS — renders null unless connected + permitted */}
      <QuickBooksFinanceSection />
    </PageShell>
  );
}

/* ─────────────────────────── inner components ─────────────────────────── */

function DeltaPill({ delta }: { delta: number }) {
  const positive = delta >= 0;
  return (
    <span
      className={[
        'inline-flex items-center gap-1 text-[12.5px] font-bold tabular-nums px-2.5 py-1 rounded-full',
        positive
          ? 'bg-lime-neon text-ink'
          : 'bg-rose-500/15 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200',
      ].join(' ')}
    >
      {positive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
      {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

function KpiTile({
  label,
  value,
  sub,
  delta,
  accent = 'default',
  errored = false,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  delta?: number | null;
  accent?: 'default' | 'coral';
  /** The backing query failed (e.g. 403 for this role) — show an honest "—"
   *  instead of a fabricated zero. */
  errored?: boolean;
}) {
  return (
    <motion.div
      variants={{
        hidden:  { opacity: 0, y: 8 },
        visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 200, damping: 22 } },
      }}
      className={[
        'rounded-3xl p-5',
        'bg-white/[0.85] dark:bg-white/[0.05] backdrop-blur-xl',
        'shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10',
      ].join(' ')}
    >
      {/* Label must be able to give way: paired with a delta pill in a narrow
       *  tile, an un-shrinkable eyebrow pushes the pill out of the card. */}
      <div className="flex items-center justify-between mb-2.5 gap-2">
        <Eyebrow className="min-w-0 truncate !tracking-[0.10em]" title={label}>{label}</Eyebrow>
        {!errored && delta != null && (
          <span
            className={[
              'inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full tabular-nums',
              delta >= 0
                ? 'bg-lime-neon text-ink'
                : 'bg-rose-500/15 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200',
            ].join(' ')}
          >
            {delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      <p
        className={[
          'font-bold text-stat-md leading-none tabular-nums tracking-[-0.025em]',
          errored ? 'text-ink/30 dark:text-white/30' : accent === 'coral' ? 'text-coral' : 'text-ink dark:text-white',
        ].join(' ')}
      >
        {errored ? '—' : value}
      </p>
      <p className="text-[12px] text-ink/55 dark:text-white/55 mt-2 truncate">
        {errored ? 'Couldn’t load for your account' : sub}
      </p>
    </motion.div>
  );
}

/**
 * Live timer wired to the user's actual open time entry on the server.
 *
 *   - Polls /api/projects/time-entries/me/open every 10s for the open entry.
 *   - If an entry exists, derives elapsed seconds from (now - entry.start_at)
 *     and ticks the display locally between polls (no fake counting).
 *   - If no entry, shows "No active session" + a link to the projects page.
 *   - If the user lacks `time_entries.view_self` (accounts/owner without an
 *     employee row), the API 403s — we render the empty state cleanly.
 */
function LocalTimer() {
  const openQ = useApi<{ entry: { start_at: string; project_id: string } | null }>(
    '/api/projects/time-entries/me/open',
  );
  const [now, setNow] = React.useState(Date.now());

  // Tick local clock every second.
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Re-poll the server every 10s so timers started elsewhere appear here.
  // 404 = user has no employee record (`not_employee`) — that won't change
  // mid-session, so stop polling instead of hammering a known-404 endpoint.
  const notEmployee = openQ.status === 404;
  React.useEffect(() => {
    if (notEmployee) return;
    const id = setInterval(() => openQ.reload(), 10_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notEmployee]);

  const entry = openQ.data?.entry ?? null;
  const elapsedSec = entry
    ? Math.max(0, Math.floor((now - new Date(entry.start_at).getTime()) / 1000))
    : 0;

  const hh = Math.floor(elapsedSec / 3600);
  const mm = Math.floor((elapsedSec % 3600) / 60).toString().padStart(2, '0');
  const ss = (elapsedSec % 60).toString().padStart(2, '0');
  const display = hh > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
  // 8h workday progress ring (sweeps once per work day).
  const progress = entry ? Math.min(1, (elapsedSec % 28_800) / 28_800) : 0;

  if (openQ.loading && !openQ.data) {
    return (
      <>
        <CircularTimer time="--:--" progress={0} label="Loading" size={180} />
        <p className="text-[12px] text-ink/55 dark:text-white/55 mt-5 text-center">
          Checking active session…
        </p>
      </>
    );
  }

  if (!entry) {
    return (
      <>
        <CircularTimer time="00:00" progress={0} label="No session" size={180} />
        <Link
          to="/dashboard/projects"
          className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-coral hover:underline underline-offset-4"
        >
          Start a timer on a project <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </>
    );
  }

  return (
    <>
      <CircularTimer time={display} progress={progress} label="Tracking" size={180} />
      <p className="text-[12px] text-ink/55 dark:text-white/55 mt-5 text-center">
        Tracking time on your active project · live from the server
      </p>
    </>
  );
}
